import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const STEPS = ['overview', 'clinical', 'blocks', 'slides'];
const VALID_STEPS = new Set(STEPS);
const LEGACY_STEP_REDIRECT = { release: 'slides' };
const STORAGE_PREFIX = 'bdsa-casebuilder-step-';

function normalizeStep(step) {
    if (LEGACY_STEP_REDIRECT[step]) return LEGACY_STEP_REDIRECT[step];
    return VALID_STEPS.has(step) ? step : null;
}

function readStoredStep(caseId) {
    if (!caseId) return null;
    try {
        const stored = sessionStorage.getItem(`${STORAGE_PREFIX}${caseId}`);
        return normalizeStep(stored);
    } catch {
        return null;
    }
}

function writeStoredStep(caseId, step) {
    if (!caseId || !VALID_STEPS.has(step)) return;
    try {
        sessionStorage.setItem(`${STORAGE_PREFIX}${caseId}`, step);
    } catch {
        // ignore
    }
}

/**
 * Workspace step navigation via ?step= query + sessionStorage per case.
 */
export function useCaseBuilderStep(caseId) {
    const [searchParams, setSearchParams] = useSearchParams();

    const step = useMemo(() => {
        const fromUrl = normalizeStep(searchParams.get('step'));
        if (fromUrl) return fromUrl;
        return readStoredStep(caseId) || 'overview';
    }, [searchParams, caseId]);

    const setStep = useCallback((nextStep) => {
        const resolved = normalizeStep(nextStep);
        if (!resolved) return;
        writeStoredStep(caseId, resolved);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('step', resolved);
            if (!next.get('ui')) next.set('ui', 'enhanced');
            return next;
        }, { replace: true });
    }, [caseId, setSearchParams]);

    // Rewrite legacy ?step=release bookmarks onto slides.
    useEffect(() => {
        if (searchParams.get('step') === 'release') {
            setStep('slides');
        }
    }, [searchParams, setStep]);

    return { step, setStep, steps: STEPS };
}
